import { Component, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { catchError, map } from 'rxjs';
import { StoreService } from 'src/app/services/store-service/store.service';
import { OperationType } from 'src/app/models/operation-types';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { User } from 'src/app/entities/user/user';
import { Uuid } from 'src/app/entities/value-objects/uuid/uuid';
import { Roles, UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';

@Component({
  selector: 'app-members-mutations',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogActions,
    MatSelectModule,
    MatDialogClose,
    MatSlideToggleModule,
    MatDialogTitle,
    MatDialogContent
  ],
  templateUrl: './members-mutations.component.html',
  styleUrl: './members-mutations.component.scss'
})
export class MembersMutationsComponent implements OnInit {
  // Dialog injection and configuration
  readonly dialogRef = inject(MatDialogRef<MembersMutationsComponent>);
  data = inject(MAT_DIALOG_DATA).operation as OperationType;
  memberId = inject(MAT_DIALOG_DATA).id as string;

  // Component state
  members: User[] = [];
  title?: string;
  submitButton?: string;

  // Form definition with validation
  protected form = new FormGroup({
    name: new FormControl<string>('', Validators.required),
    pass: new FormControl<string>('', Validators.required),
    status: new FormControl<boolean>(true),
    mail: new FormControl<string>('', [Validators.required, Validators.email]),
    roles: new FormControl<Roles[]>([Roles.chronos_tracker], Validators.required),
  });

  constructor(
    protected userRep: UserRepository,
    protected store: StoreService,
    private snackbarService: SnackbarService,
  ) {
    this.store.users.subscribe((users) => {
      this.members = users;
    });
  }

  /**
   * Initializes the component based on the operation type.
   * Sets up the form with existing data if updating a member.
   */
  ngOnInit(): void {
    switch (this.data) {
      case OperationType.CREATE:
        this.title = 'Create Member';
        this.submitButton = 'Create';
        break;
      case OperationType.UPDATE: {
        this.title = 'Update Member';
        this.submitButton = 'Update';
        const currentMember = this.members.find(m => m.id === this.memberId);

        if (currentMember) {
          this.form.patchValue({
            name: currentMember.name,
            status: currentMember.status,
            mail: currentMember.mail,
            roles: currentMember.roles,
          });
        }
        break;
      }
    }
  }

  /**
   * Handles form submission for both create and update operations
   * Creates or updates a member based on the operation type
   */
  memberSubmit(): void {
    const form = this.form.value;
    console.log(form.name)

    const permissions = { update: true, delete: true };
    const newMember = new User(
      Uuid.generateId(),
      form.name || '',
      form.status || true,
      form.mail || '',
      form.roles || [Roles.chronos_tracker],
      permissions
    );
    const updatedMember = new User(
      this.memberId,
      form.name || '',
      form.status || true,
      form.mail || '',
      form.roles || [Roles.chronos_tracker],
      permissions
    );

    switch (this.data) {
      case OperationType.CREATE:
        this.addMember(newMember, form.pass || undefined);
        break;
      case OperationType.UPDATE: {
        this.updateMember(updatedMember);
        break;
      }
    }
  }

  addMember(member: User, pass?: string): void {
    this.userRep.addNewUser(member, pass).pipe(
      catchError((err) => {
        this.snackbarService.showError(`${err.error.message || 'Error'}`);
        return []
      }),
      map(() => {
        this.store.addEntity(member);
        this.snackbarService.showSuccess('Member created');
      })
    ).subscribe()
  }

  updateMember(member: User): void {
    this.userRep.updateUser(member).pipe(
      catchError((err) => {
        console.error(err);
        return []
      }),
      map(() => {
        this.store.updateEntity(member);
        this.snackbarService.showSuccess('Member updated');
      })
    ).subscribe()
  }
}
